import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { usePopper } from 'react-popper';
import { useEditorContext } from 'app/state/contexts/EditorContext';
import { useEditorNavigate, useEditorSearch, useRequiredParams } from 'app/router/params';
import { useSimpleVersions } from 'entities/document';
import Tippy from '@tippyjs/react';

type LockOwner = {
  firstName?: string;
  lastName?: string;
};

type ReadOnlyMarkProps = {
  readOnly?: boolean;
  isViewingVersion?: boolean;
  lockOwner?: LockOwner;
};

type VersionItem = { documentRevisionId: string; name?: string };

const ViewingVersionTooltip = () => {
  const { t } = useTranslation('editor');
  const navigate = useEditorNavigate();
  const { documentId } = useRequiredParams('documentId');
  const { revision } = useEditorSearch();
  const { data: versions } = useSimpleVersions(documentId);
  const versionName = (versions as VersionItem[] | undefined)?.find((version) => version.documentRevisionId === revision)?.name;

  const goToDraft = () => {
    navigate({ search: (prev) => ({ ...prev, revision: undefined, sectionRevision: undefined }) });
  };

  return (
    <div className="gaby-tooltip">
      <p>{versionName ? t('readonly.viewing-version', { name: versionName }) : t('readonly.viewing-version.unknown')}</p>
      <button type="button" className="btn btn-secondary btn-sm mt-2" onClick={goToDraft}>
        {t('readonly.go-to-draft')}
      </button>
    </div>
  );
};

const ReadOnlyMark = React.memo(({ readOnly, isViewingVersion = false, lockOwner }: ReadOnlyMarkProps) => {
  const { t } = useTranslation('editor');

  const { editorContainerRef } = useEditorContext();
  const { firstName, lastName } = lockOwner || {};
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const { styles, attributes } = usePopper(editorContainerRef.current, popperElement, {
    placement: 'right-start',
  });

  if (!readOnly) {
    return null;
  }

  const lockedByContent = (
    <p className="gaby-tooltip">{firstName && lastName ? `${t('readonly.lockedby')} ${firstName} ${lastName}` : `${t('readonly.lockedby.unknown')}`}</p>
  );

  return (
    <Tippy content={isViewingVersion ? <ViewingVersionTooltip /> : lockedByContent} placement="left" interactive={isViewingVersion}>
      <div ref={setPopperElement} style={styles.popper} {...attributes.popper}>
        <div className="read-only-mark">
          <i className={`fa-regular ${isViewingVersion ? 'fa-clock-rotate-left' : 'fa-eye'}`} />
        </div>
      </div>
    </Tippy>
  );
});

export default ReadOnlyMark;
